/**
 * Camera Error Handler Utility
 * Centralizes camera error handling logic used across multiple hooks and components
 */

import { tUnsafe } from '../i18n';
import { logger } from './logger';

const cameraLogger = logger.clone({ labels: { component: 'CameraErrorHandler' }});

/**
 * Standard camera error messages for consistent UX
 */
const CAMERA_ERROR_KEYS = {
  NOT_ALLOWED: 'cameraErrors.notAllowed',
  SECURITY: 'cameraErrors.security',
  NOT_FOUND: 'cameraErrors.notFound',
  NOT_READABLE: 'cameraErrors.notReadable',
  OVERCONSTRAINED: 'cameraErrors.overconstrained',
  TIMEOUT: 'cameraErrors.timeout',
  VIDEO_STALLED: 'cameraErrors.videoStalled',
  INTERRUPTED: 'cameraErrors.interrupted',
  INITIALIZATION_FAILED: 'cameraErrors.initializationFailed',
  STREAM_UNAVAILABLE: 'cameraErrors.streamUnavailable',
  GENERIC: 'cameraErrors.generic',
} as const;

const CAMERA_ERROR_ACTION_KEYS = {
  NOT_ALLOWED: 'cameraErrors.notAllowedAction',
  SECURITY: 'cameraErrors.securityAction',
  NOT_FOUND: 'cameraErrors.notFoundAction',
  NOT_READABLE: 'cameraErrors.notReadableAction',
  OVERCONSTRAINED: 'cameraErrors.overconstrainedAction',
  TIMEOUT: 'cameraErrors.timeoutAction',
  VIDEO_STALLED: 'cameraErrors.videoStalledAction',
  INTERRUPTED: 'cameraErrors.interruptedAction',
  GENERIC: 'cameraErrors.genericAction',
} as const;

export type CameraErrorMessageKey =
  (typeof CAMERA_ERROR_KEYS)[keyof typeof CAMERA_ERROR_KEYS];

export type CameraErrorType =
  | 'permission'
  | 'security'
  | 'not-found'
  | 'in-use'
  | 'unsupported'
  | 'stalled'
  | 'interrupted'
  | 'unknown';

export interface CameraError {
  name: string;
  message: string;
  type: CameraErrorType;
  userMessageKey: CameraErrorMessageKey;
  userMessage: string;
  isRecoverable: boolean;
  suggestedAction?: string;
}

const resolveCameraMessage = (key: keyof typeof CAMERA_ERROR_KEYS): string =>
  tUnsafe(CAMERA_ERROR_KEYS[key]);

const resolveCameraAction = (
  key: keyof typeof CAMERA_ERROR_ACTION_KEYS
): string => tUnsafe(CAMERA_ERROR_ACTION_KEYS[key]);

/**
 * Read an error's `name` without relying on `instanceof Error`.
 *
 * getUserMedia rejects with a DOMException, which is not an Error subclass in
 * every realm (cross-realm throws, jsdom). An `instanceof` gate silently
 * downgrades a real NotAllowedError to an unclassified failure.
 */
export function getCameraErrorName(error: unknown): string {
  if (error && typeof error === 'object' && 'name' in error) {
    const { name } = error as { name?: unknown };
    if (typeof name === 'string') {
      return name;
    }
  }
  return '';
}

/**
 * Read an error's `message` under the same cross-realm constraints as
 * getCameraErrorName.
 */
export function getCameraErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string') {
      return message;
    }
  }
  return '';
}

/**
 * Get camera error type from error
 *
 * Message matching is case-insensitive: browser and utility phrasings vary
 * ("Permission denied" vs "camera permissions"), and a case-sensitive test
 * misclassifies a denial as an unknown failure.
 */
export function getCameraErrorType(
  error: Error | unknown
): CameraErrorType {
  const name = getCameraErrorName(error);
  const message = getCameraErrorMessage(error).toLowerCase();

  if (name === 'SecurityError') {
    return 'security';
  }

  // Readiness failures raised by cameraStreamReadiness after getUserMedia
  // succeeded: the browser granted a track but usable video never arrived,
  // or the track ended underneath the page. Both are client-local and are
  // recovered by re-acquiring the camera, never by changing constraints.
  if (name === 'CameraVideoStalledError') {
    return 'stalled';
  }

  if (name === 'CameraInterruptedError') {
    return 'interrupted';
  }

  if (name === 'NotAllowedError' || message.includes('permission')) {
    return 'permission';
  }

  if (name === 'NotFoundError' || message.includes('not found')) {
    return 'not-found';
  }

  if (name === 'NotReadableError' || message.includes('in use')) {
    return 'in-use';
  }

  if (name === 'NotSupportedError' || message.includes('not supported')) {
    return 'unsupported';
  }

  return 'unknown';
}

/**
 * Maps camera error names to user-friendly messages
 */
export function mapCameraError(error: Error | unknown): CameraError {
  cameraLogger.error('Camera error occurred:', { error: error instanceof Error ? error : new Error(`${error}`) });

  const name = getCameraErrorName(error);
  const message = getCameraErrorMessage(error);
  const type = getCameraErrorType(error);

  if (name || message) {
    switch (type) {
      case 'permission':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.NOT_ALLOWED,
          userMessage: resolveCameraMessage('NOT_ALLOWED'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('NOT_ALLOWED'),
        };

      case 'security':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.SECURITY,
          userMessage: resolveCameraMessage('SECURITY'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('SECURITY'),
        };

      case 'not-found':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.NOT_FOUND,
          userMessage: resolveCameraMessage('NOT_FOUND'),
          isRecoverable: false,
          suggestedAction: resolveCameraAction('NOT_FOUND'),
        };

      case 'in-use':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.NOT_READABLE,
          userMessage: resolveCameraMessage('NOT_READABLE'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('NOT_READABLE'),
        };

      case 'stalled':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.VIDEO_STALLED,
          userMessage: resolveCameraMessage('VIDEO_STALLED'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('VIDEO_STALLED'),
        };

      case 'interrupted':
        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.INTERRUPTED,
          userMessage: resolveCameraMessage('INTERRUPTED'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('INTERRUPTED'),
        };

      default:
        if (name === 'OverconstrainedError') {
          return {
            name,
            message,
            type,
            userMessageKey: CAMERA_ERROR_KEYS.OVERCONSTRAINED,
            userMessage: resolveCameraMessage('OVERCONSTRAINED'),
            isRecoverable: true,
            suggestedAction: resolveCameraAction('OVERCONSTRAINED'),
          };
        }

        // Check if it's a timeout error
        if (message.toLowerCase().includes('timeout')) {
          return {
            name: 'TimeoutError',
            message,
            type,
            userMessageKey: CAMERA_ERROR_KEYS.TIMEOUT,
            userMessage: resolveCameraMessage('TIMEOUT'),
            isRecoverable: true,
            suggestedAction: resolveCameraAction('TIMEOUT'),
          };
        }

        return {
          name,
          message,
          type,
          userMessageKey: CAMERA_ERROR_KEYS.INITIALIZATION_FAILED,
          // Never surface the raw browser message: it is untranslated and
          // often exposes device labels.
          userMessage: resolveCameraMessage('INITIALIZATION_FAILED'),
          isRecoverable: true,
          suggestedAction: resolveCameraAction('GENERIC'),
        };
    }
  }

  return {
    name: 'UnknownError',
    message: String(error),
    type,
    userMessageKey: CAMERA_ERROR_KEYS.GENERIC,
    userMessage: resolveCameraMessage('GENERIC'),
    isRecoverable: true,
    suggestedAction: resolveCameraAction('GENERIC'),
  };
}

/**
 * Hook for consistent camera error handling
 */
export function useCameraErrorHandler() {
  const handleCameraError = (error: Error | unknown): CameraError => {
    const cameraError = mapCameraError(error);

    // Log the structured error for debugging
    cameraLogger.error('Handled camera error:', {
      error: error instanceof Error ? error : new Error(`${error}`),
      labels: { mappedError: JSON.stringify(cameraError) }
    });

    return cameraError;
  };

  return { handleCameraError };
}

/**
 * Utility to check if an error is a camera permission error
 */
export function isCameraPermissionError(error: Error | unknown): boolean {
  return getCameraErrorType(error) === 'permission';
}

/**
 * Utility to check if an error is recoverable
 */
export function isCameraErrorRecoverable(error: Error | unknown): boolean {
  const cameraError = mapCameraError(error);
  return cameraError.isRecoverable;
}

/**
 * Create a timeout promise for camera operations
 */
export function createCameraTimeout(timeoutMs: number = 60000): Promise<never> {
  return new Promise((_, reject) =>
    setTimeout(
      () =>
        reject(new Error(`Camera initialization timeout after ${timeoutMs}ms`)),
      timeoutMs
    )
  );
}

/**
 * Race a camera operation with a timeout
 */
export async function withCameraTimeout<T>(
  operation: Promise<T>,
  timeoutMs: number = 60000
): Promise<T> {
  return Promise.race([operation, createCameraTimeout(timeoutMs)]);
}
